/**
 * Primitives shared across all Firestore document types.
 *
 * Timestamps are represented as a structural type compatible with both
 * `firebase/firestore` (web SDK) and `firebase-admin/firestore` (admin SDK).
 * Each SDK's `Timestamp` class implements `.toDate()` / `.toMillis()`, so
 * consumers can depend only on this structural contract.
 */
export interface Timestamp {
  toDate(): Date;
  toMillis(): number;
  seconds: number;
  nanoseconds: number;
}

/**
 * ISO-8601 date string (e.g. "2026-04-18"). Used for plan/actual dates where
 * only the calendar day matters, independent of TZ.
 */
export type IsoDate = string;

/** Firebase Auth UID. */
export type UserId = string;

/** Firestore document ID. */
export type DocId = string;

/**
 * Project role assigned within `members[uid].role`.
 *
 * Hierarchy: `owner` ≥ `admin` ≥ `editor` ≥ `viewer`.
 *   owner  — project creator; immutable, sole authority over `ownerUserId`.
 *   admin  — manages project settings, members, Backlog, schedules.
 *   editor — mutates tasks/runs/executions; cannot edit project settings.
 *   viewer — read-only.
 */
export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

/** Map of UID → role for fast Security Rules checks on denormalized docs. */
export type MemberRolesMap = Record<UserId, Role>;

/** Japanese display labels for project roles (UI surface). */
export const ROLE_LABELS_JA: Record<Role, string> = {
  owner: 'オーナー',
  admin: '管理者',
  editor: '編集者',
  viewer: '閲覧者',
};

/** Roles that can edit project settings (info, Backlog, schedules). */
export const SETTINGS_MANAGER_ROLES: readonly Role[] = ['owner', 'admin'];

/** Returns the set of roles a caller may invite. */
export function invitableRolesFor(callerRole: Role | undefined): readonly Role[] {
  if (callerRole === 'owner' || callerRole === 'admin') {
    return ['admin', 'editor', 'viewer'];
  }
  if (callerRole === 'editor') {
    return ['editor', 'viewer'];
  }
  return [];
}

export function canManageProjectSettings(role: Role | undefined): boolean {
  return role === 'owner' || role === 'admin';
}
